import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, getToken, setToken } from "@/lib/api";

const AppContext = createContext(null);

export const useApp = () => useContext(AppContext);

export const AppProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [workspaces, setWorkspaces] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [activeWorkspace, setActiveWorkspaceState] = useState(null);
  const [activeWebsite, setActiveWebsiteState] = useState(null);

  const loadWorkspaces = useCallback(async (preferredWsId, preferredSiteId) => {
    const { data: wss } = await api.get("/workspaces");
    setWorkspaces(wss);
    let ws = wss.find((w) => w.id === (preferredWsId || localStorage.getItem("scc_ws"))) || wss[0] || null;
    setActiveWorkspaceState(ws);
    if (ws) {
      localStorage.setItem("scc_ws", ws.id);
      const { data: sites } = await api.get(`/workspaces/${ws.id}/websites`);
      setWebsites(sites);
      let site = sites.find((s) => s.id === (preferredSiteId || localStorage.getItem("scc_site"))) || sites[0] || null;
      setActiveWebsiteState(site);
      if (site) localStorage.setItem("scc_site", site.id);
      return { ws, site, workspaces: wss, websites: sites };
    }
    setWebsites([]);
    setActiveWebsiteState(null);
    return { ws: null, site: null, workspaces: wss, websites: [] };
  }, []);

  const setActiveWorkspace = useCallback(async (ws) => {
    setActiveWorkspaceState(ws);
    localStorage.setItem("scc_ws", ws.id);
    const { data: sites } = await api.get(`/workspaces/${ws.id}/websites`);
    setWebsites(sites);
    const site = sites[0] || null;
    setActiveWebsiteState(site);
    if (site) localStorage.setItem("scc_site", site.id);
    else localStorage.removeItem("scc_site");
  }, []);

  const setActiveWebsite = useCallback((site) => {
    setActiveWebsiteState(site);
    if (site) localStorage.setItem("scc_site", site.id);
  }, []);

  useEffect(() => {
    (async () => {
      if (!getToken()) {
        setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await loadWorkspaces();
      } catch (e) {
        setToken(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [loadWorkspaces]);

  const login = async (token, userData) => {
    setToken(token);
    setUser(userData);
    return await loadWorkspaces();
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) { /* stateless */ }
    setToken(null);
    setUser(null);
    setWorkspaces([]);
    setWebsites([]);
    setActiveWorkspaceState(null);
    setActiveWebsiteState(null);
    localStorage.removeItem("scc_ws");
    localStorage.removeItem("scc_site");
  };

  return (
    <AppContext.Provider value={{
      user, setUser, loading, workspaces, websites, activeWorkspace, activeWebsite,
      setActiveWorkspace, setActiveWebsite, loadWorkspaces, login, logout,
    }}>
      {children}
    </AppContext.Provider>
  );
};
